import {
  AUTO_MOBILE_PARTY_SIZE_THRESHOLD,
  HENNA_TYPE_OPTIONS,
  JAGUAR_HENNA_EXTRA_PRICE,
  SERVICE_MODE,
  TIME_SLOT_INTERVAL_MINUTES,
} from "./constants";

export const isEmpty = (value) => {
  return value === null || value === undefined || String(value).trim() === "";
};

export const isValidEmail = (email) => {
  if (isEmpty(email)) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());
};

export const isValidPhoneNumber = (phoneNumber) => {
  if (isEmpty(phoneNumber)) return false;

  const normalized = String(phoneNumber).replace(/[^\d]/g, "");
  return normalized.length >= 10 && normalized.length <= 15;
};

export const isStrongEnoughPassword = (password) => {
  if (isEmpty(password)) return false;
  return String(password).length >= 8;
};

export const isValidTimeString = (time) => {
  if (isEmpty(time)) return false;
  return /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/.test(String(time).trim());
};

export const normalizeTimeString = (time) => {
  if (!isValidTimeString(time)) return "";

  const [hours, minutes] = String(time).split(":");
  return `${hours}:${minutes}`;
};

export const timeStringToMinutes = (time) => {
  if (!isValidTimeString(time)) return NaN;

  const [hours, minutes] = normalizeTimeString(time).split(":").map(Number);
  return hours * 60 + minutes;
};

export const isTimeIncrementValid = (
  time,
  interval = TIME_SLOT_INTERVAL_MINUTES
) => {
  const minutes = timeStringToMinutes(time);

  if (Number.isNaN(minutes)) return false;
  return minutes % interval === 0;
};

export const isEndTimeAfterStartTime = (startTime, endTime) => {
  const start = timeStringToMinutes(startTime);
  const end = timeStringToMinutes(endTime);

  if (Number.isNaN(start) || Number.isNaN(end)) return false;
  return end > start;
};

export const calculatePartySize = (numOfAdults = 0, numOfChildren = 0) => {
  const adults = Number(numOfAdults) || 0;
  const children = Number(numOfChildren) || 0;
  return adults + children;
};

export const calculateBookingItemPartySize = (item = {}) => {
  return calculatePartySize(item.numOfAdults, item.numOfChildren);
};

export const resolveServiceModeByPartySize = (
  numOfAdults = 0,
  numOfChildren = 0
) => {
  const partySize = calculatePartySize(numOfAdults, numOfChildren);
  return partySize >= AUTO_MOBILE_PARTY_SIZE_THRESHOLD
    ? SERVICE_MODE.MOBILE
    : SERVICE_MODE.STUDIO;
};

export const resolveServiceModeByTotalPartySize = (partySize = 0) => {
  return Number(partySize) >= AUTO_MOBILE_PARTY_SIZE_THRESHOLD
    ? SERVICE_MODE.MOBILE
    : SERVICE_MODE.STUDIO;
};

export const validateAddress = (address = {}) => {
  const errors = {};

  if (isEmpty(address.line1)) errors.line1 = "Address line 1 is required.";
  if (isEmpty(address.city)) errors.city = "City is required.";
  if (isEmpty(address.state)) errors.state = "State is required.";
  if (isEmpty(address.zip)) errors.zip = "ZIP code is required.";

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const calculateBookingItemDerivedValues = (item = {}, services = []) => {
  const resolvedService =
    services.find((service) => Number(service.id) === Number(item.serviceId)) ||
    null;

  const numOfAdults = Math.max(0, Number(item.numOfAdults) || 0);
  const numOfChildren = Math.max(0, Number(item.numOfChildren) || 0);
  const partySize = calculatePartySize(numOfAdults, numOfChildren);

  const basePrice = Number(resolvedService?.servicePrice) || 0;
  const baseDuration = Number(resolvedService?.serviceDurationMinutes) || 0;

  const adultPrice = basePrice * numOfAdults;
  const childPrice = basePrice * 0.5 * numOfChildren;
  let bookingPrice = adultPrice + childPrice;

  if (item.hennaType === "Jaguar") {
    bookingPrice += JAGUAR_HENNA_EXTRA_PRICE;
  }

  const adultDuration = baseDuration * numOfAdults;
  const childDuration = baseDuration * 0.5 * numOfChildren;
  const bookingDurationMinutes = Math.round(adultDuration + childDuration);

  return {
    service: resolvedService,
    serviceId: item.serviceId || "",
    hennaType: item.hennaType || "",
    numOfAdults,
    numOfChildren,
    partySize,
    bookingPrice: Number(bookingPrice.toFixed(2)),
    bookingDurationMinutes,
  };
};

export const calculateBookingTotals = (bookingItems = [], services = []) => {
  const items = (bookingItems || []).map((item, index) => {
    const derived = calculateBookingItemDerivedValues(item, services);

    return {
      ...item,
      ...derived,
      sortOrder:
        item?.sortOrder !== undefined && item?.sortOrder !== null
          ? item.sortOrder
          : index,
    };
  });

  const totalPartySize = items.reduce(
    (sum, item) => sum + (Number(item.partySize) || 0),
    0
  );

  const totalPrice = items.reduce(
    (sum, item) => sum + (Number(item.bookingPrice) || 0),
    0
  );

  const totalDurationMinutes = items.reduce(
    (sum, item) => sum + (Number(item.bookingDurationMinutes) || 0),
    0
  );

  return {
    items,
    totalPartySize,
    totalPrice: Number(totalPrice.toFixed(2)),
    totalDurationMinutes,
    serviceMode: resolveServiceModeByTotalPartySize(totalPartySize),
  };
};

export const validateBookingPayload = ({
  bookingDate,
  startTime,
  endTime,
  bookingItems = [],
  serviceMode,
  guest = null,
  onsiteAddress = null,
}) => {
  const errors = {};
  const bookingItemErrors = [];

  const normalizedItems = Array.isArray(bookingItems) ? bookingItems : [];

  normalizedItems.forEach((item, index) => {
    const itemErrors = {};
    const partySize = calculateBookingItemPartySize(item);

    if (isEmpty(item?.serviceId)) {
      itemErrors.serviceId = "Please select a service.";
    }

    if (isEmpty(item?.hennaType)) {
      itemErrors.hennaType = "Please select a henna type.";
    } else if (!HENNA_TYPE_OPTIONS.includes(item.hennaType)) {
      itemErrors.hennaType = "Please select a valid henna type.";
    }

    if (partySize <= 0) {
      itemErrors.partySize =
        "Each selected service must include at least one person.";
    }

    if (Object.keys(itemErrors).length > 0) {
      bookingItemErrors[index] = itemErrors;
    }
  });

  if (normalizedItems.length === 0) {
    errors.bookingItems = [{ serviceId: "Please select at least one service." }];
  } else if (bookingItemErrors.length > 0) {
    errors.bookingItems = bookingItemErrors;
  }

  if (isEmpty(bookingDate)) {
    errors.bookingDate = "Please select a booking date.";
  }

  if (!isValidTimeString(startTime)) {
    errors.startTime = "Please select a valid start time.";
  }

  if (!isValidTimeString(endTime)) {
    errors.endTime = "Please select a valid end time.";
  }

  if (
    isValidTimeString(startTime) &&
    isValidTimeString(endTime) &&
    !isEndTimeAfterStartTime(startTime, endTime)
  ) {
    errors.endTime = "End time must be after start time.";
  }

  const totals = calculateBookingTotals(normalizedItems, []);
  const resolvedServiceMode =
    serviceMode || resolveServiceModeByTotalPartySize(totals.totalPartySize);

  if (totals.totalPartySize <= 0) {
    errors.partySize = "At least one person must be included in the booking.";
  }

  if (resolvedServiceMode === SERVICE_MODE.MOBILE) {
    const addressValidation = validateAddress(onsiteAddress || {});
    if (!addressValidation.isValid) {
      errors.onsiteAddress = addressValidation.errors;
    }
  }

  if (guest) {
    if (isEmpty(guest.firstName)) {
      errors.guestFirstName = "Guest first name is required.";
    }

    if (isEmpty(guest.lastName)) {
      errors.guestLastName = "Guest last name is required.";
    }

    if (!isValidEmail(guest.email)) {
      errors.guestEmail = "A valid guest email is required.";
    }

    if (!isEmpty(guest.phoneNumber) && !isValidPhoneNumber(guest.phoneNumber)) {
      errors.guestPhoneNumber = "Please enter a valid phone number.";
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    partySize: totals.totalPartySize,
    serviceMode: resolvedServiceMode,
  };
};

const validators = {
  isEmpty,
  isValidEmail,
  isValidPhoneNumber,
  isStrongEnoughPassword,
  isValidTimeString,
  normalizeTimeString,
  timeStringToMinutes,
  isTimeIncrementValid,
  isEndTimeAfterStartTime,
  calculatePartySize,
  calculateBookingItemPartySize,
  calculateBookingItemDerivedValues,
  calculateBookingTotals,
  resolveServiceModeByPartySize,
  resolveServiceModeByTotalPartySize,
  validateAddress,
  validateBookingPayload,
};

export default validators;